import { Button, Spinner, Stack, Text, useDisclosure } from '@chakra-ui/react';
import { DocumentType } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { DOCUMENT_TYPES_REQUIRING_APPROVAL, getMissingDocumentTypes } from '@pelicargo/utils';
import { useMemo } from 'react';
import { HiOutlineCheck } from 'react-icons/hi2';
import { useParams } from 'react-router-dom';

import { trpc, TrpcOutputs } from '../../config/trpc';
import { useDocuments } from '../../hooks/documents/useDocuments';
import { useTargetAccount } from '../../hooks/useTargetAccount';
import { DocumentRow } from './DocumentRow';
import { DocumentsModal } from './DocumentsModal';

type Document = TrpcOutputs['getQuote']['request']['documents'][number];

type Props = {
  name: string;
  documentTypes: DocumentType[];
  requestId?: number;
  isApprovable?: boolean;
};

export const DocumentsForm = ({ documentTypes: defaultDocumentTypes, requestId, isApprovable = false }: Props) => {
  const disclosure = useDisclosure();
  const [documents, isLoading] = useDocuments({ requestId });
  const { targetAccount } = useTargetAccount();
  const { quoteId } = useParams<{ requestId: string; quoteId: string }>();
  const { mutateAsync: approveDocuments, isLoading: isApproving } = trpc.approveDocuments.useMutation();

  const cleanedDocuments = useMemo(() => {
    const missingDocumentTypes = getMissingDocumentTypes(documents, defaultDocumentTypes);

    // Include all current documents in the list. If a document type is not in the list, it will be added.
    const finalDocuments = [
      ...documents,
      ...missingDocumentTypes.map((type) => ({ type }) as Partial<Document>),
    ].filter((doc) => doc?.type || doc?.id);

    return finalDocuments;
  }, [defaultDocumentTypes, documents]);

  const nonAccountDocuments = documents.filter((doc) => doc.account_id !== targetAccount?.id);
  const isApproveButtonDisabled = nonAccountDocuments
    .filter((doc) => DOCUMENT_TYPES_REQUIRING_APPROVAL.includes(doc.type))
    .every((doc) => !!doc.approved_at);

  const renderDocumentRow = (document: Partial<Document>) => <DocumentRow key={document.id} document={document} />;

  if (isLoading) return <Spinner />;

  const buttonLabel = documents.length > 0 ? 'Manage Documents' : 'Add Documents';

  const handleApproveAndNotify = async () => {
    const nonApprovedDocuments = documents.filter(
      (doc) =>
        doc.id &&
        !doc.approved_at &&
        doc.account_id !== targetAccount?.id &&
        DOCUMENT_TYPES_REQUIRING_APPROVAL.includes(doc.type),
    );
    await approveDocuments({
      documentIds: nonApprovedDocuments.map((doc) => doc.id),
      quote_id: Number(quoteId),
      shouldNotify: true,
    });
  };

  return (
    <>
      <Stack>
        <Text fontSize="md" fontWeight="bold">
          Your Documents
        </Text>
        <Stack>{cleanedDocuments.map(renderDocumentRow)}</Stack>
        <Stack direction={{ base: 'column', md: 'row' }}>
          <Button variant="outline" colorScheme="primary" width="full" onClick={disclosure.onOpen}>
            {buttonLabel}
          </Button>
          <Show if={isApprovable}>
            <Button
              variant="solid"
              colorScheme="green"
              title={isApproveButtonDisabled ? 'No documents to approve' : undefined}
              onClick={handleApproveAndNotify}
              isLoading={isApproving}
              isDisabled={isApproveButtonDisabled}
              w="full"
              leftIcon={<HiOutlineCheck />}
            >
              Approve & Notify
            </Button>
          </Show>
        </Stack>
      </Stack>
      <DocumentsModal
        isOpen={disclosure.isOpen}
        onClose={disclosure.onClose}
        documentTypes={defaultDocumentTypes}
        defaultDocuments={documents}
        isApprovable={isApprovable}
      />
    </>
  );
};
